import { errorCodes } from '../../utils/errorCodes';
import { tableTypes } from '../../components/Pages/Reports/Table';
import { cardTypes } from '../../components/Metrics/cardTypes';
import { filterIds, reportGroup, reportViews } from '../../utils/filters';
import {
    AGENT_ACTIVITY_STATUS,
    ANONYMIZATION_TYPES,
    CARD_SECTIONS,
    GROUP_BY,
    PAGING_ENTITIES,
    REPORT_GUIDANCE_TYPES,
    USER_ROLES
} from '../../utils/constants';
import { NOTIFICATION_TYPES, VALIDATION_CODES } from '../../components/Pages/Keywords/constants';
import { AVERAGE_KEY } from '../../utils/keywordsChart';

const callTypes = {
    all: 'All call types',
    product: 'Product calls',
    service: 'Service calls',
    productConversion: 'Product calls Conversion',
    serviceConversion: 'Service calls Conversion'
};

const overviewWordsCardConfig = () => ({
    labels: {
        say: 'Avg. Do-Say Usage',
        dontSay: 'Avg. Don’t-Say Words',
        [AVERAGE_KEY]: 'Do-Say Usage',
        keywords: 'Keywords',
        emptyFilter: 'Select...'
    }
});

const wordsCardConfig = name => ({
    columns: {
        name,
        say: 'Avg. Do-Say Usage',
        trend: '',
        calls: 'Calls'
    }
});

const overviewSpeechRatioCardConfig = () => ({
    labels: {
        speechRatio: 'Speech Ratio',
        speechRate: 'Speech Rate'
    }
});

const speechRationCardConfig = name => ({
    columns: {
        name,
        speechRatio: {
            left: 'Talking',
            right: 'Listening'
        },
        speechRate: 'Speed(W/min)'
    }
});

const gamificationScorePointsConfig = () => ({
    columns: {
        name: 'Agent',
        place: 'Position',
        points: 'Points',
        avgPoints: 'Points/Call'
    }
});

const callDurationStatsConfig = () => ({
    count: 'Calls:',
    min: 'Min:',
    avg: 'Avg.:',
    sec: 'sec',
    buttonLabel: 'See calls'
});

const callAmountConfig = () => ({
    total: 'Total: %{count}'
});

const callTopics = {
    new_contract_or_extension: 'New contract or extension',
    contract_change: 'Contract changes',
    contract_issue: 'Contract Issues',
    contract_cancellation: 'Contract termination',
    disruption: 'Disruption',
    technical_problem: 'Technical Problem',
    current_order: 'Current Order',
    invoice_issue: 'Invoice Issues',
    unknown: 'Unknown',
    mobile: 'Mobile',
    internet_landline: 'Internet',
    internet: 'Internet',
    tv: 'TV',
    sim_card: 'SIM Card',
    router: 'Router',
    giga_product: 'Giga Product',
    unitymedia: 'UnityMedia',
    appointment: 'Appointment',
    move: 'Move',
    my_vodafone: 'My Vodafone'
};

const callTopicsOverviewCardConfig = {
    columns: {
        topic: 'Topic',
        frequency: 'Frequency',
        amount: 'Calls',
        saleRatio: 'Sale',
        conversion: 'Conversion'
    }
};
const agentSalesByCallTopicsConfig = {
    columns: {
        topic: 'Topic',
        sales: 'Sales',
        calls: 'Calls',
        agent: 'Agent'
    }
};

function sentimentTeamComparisonConfig() {
    return {
        labels: {
            keywords: 'Teams selected',
            sentimentComparisonFilter: 'All Teams Average',
            emptyFilter: 'Select...'
        },
        columns: {
            teams: 'Teams',
            customerSentiment: "Customer's Sentiment",
            agentSentiment: "Agent's Sentiment"
        },
        legend: {
            positive: 'positive Sentiment',
            neutral: 'neutral Sentiment',
            negative: 'negative Sentiment'
        }
    };
}

function sentimentTrendCustomerConfig() {
    return {
        labels: {
            sentimentTrendFilter: 'Teams',
            average: 'Average All Teams (+)',
            averagePositive: 'Average All Teams (+)',
            averageNegative: 'Average All Teams (-)'
        }
    };
}

export const en = {
    login: {
        title: 'Login',
        auth_error: {
            general: 'Please check your credentials or try again later',
            expired:
                'Your password has expired. Update your password using the "Forgot Password" feature on the login screen.'
        },
        email: {
            label: 'Email'
        },
        password: {
            label: 'Password'
        },
        submit: {
            title: 'Sign in',
            in_progress: 'Connecting'
        },
        forgotPassword: 'Forgot your password?'
    },
    recovery: {
        title: 'Forgot your password?',
        description:
            'Please enter the email address you used to login. We will send you an email with instructions on how to reset your password.',
        email: {
            label: 'E-Mail'
        },
        password: {
            label: 'Password'
        },
        submit: {
            title: 'Reset your password',
            in_progress: 'Connecting'
        },
        sent: {
            title: 'Email sent',
            description: "We've sent you an email with a link to reset your password."
        },
        no_email:
            "If you didn't receive an email: Check if the email is in your spam email box or if the email you entered might be incorrect.",
        back: 'Return',
        reset: {
            title: 'Reset your password',
            description: 'Set a new password for the account.',
            weak_password: 'Password is too weak.',
            recommended: 'For your own security, please choose a password that you have never used before.'
        },
        errors: {
            unknown_error: 'Unfortunately, an unknown error has occurred.',
            password_too_weak: 'Password is too weak.',
            password_too_short: 'The password is too short (minimum 12 characters).',
            err_password_repeated:
                "Password cannot be reused. Please choose a new password that hasn't been used recently."
        }
    },
    general: {
        privacyInfo: {
            graphs: 'The underlying data does not meet the data privacy criteria and cannot be displayed.'
        },
        error: {
            unknown: 'An unknown error has occurred.',
            statsError:
                'Bitte beachten: Die Statistiken ab dem 1. Februar sind momentan nicht verfügbar. Wir arbeiten an einer Lösung die Daten so schnell wie möglich wiederherzustellen!',
            reportsError:
                'Oops! We are having a major outage that is affecting our product. Our team is working on it, and we will update you as soon as possible.'
        },
        modal: {
            title: {
                hierarchySelection: '[DE]Use the following hierarchy to navigate and select teams.'
            },

            buttons: {
                clear: 'Clear',
                apply: 'Apply',
                select: 'Select',
                cancel: 'Cancel',
                save: 'Save'
            },

            searchPlaceholder: 'Search'
        }
    },
    whatsNew: {
        title: "What's new?",
        close: 'Close',
        new: 'New',
        markAllAsRead: 'Mark all as read'
    },
    nav: {
        dashboard: 'Dashboard',
        scorecard: 'Scorecard',
        i2x_metrics: 'I2X Key Figures',
        overview: 'Overview',
        hcsat: 'CSAT',
        reports: 'Reports',
        keywordManager: 'Keyword Manager',
        userManagement: 'Teams',
        calls: 'Calls',
        today: 'Today',
        sentimentAnalysis: 'Sentiment',
        sales: 'Sales',
        kpiManager: 'KPI Manager',
        qualityManagement: 'QM',
        qualityAssurance: 'QA',
        callHistory: 'Call History'
    },
    sales: {
        filters: {
            button: 'Refresh'
        },
        error: 'Oops! Please try refreshing the page to see the results.',
        sales_in_all_categories: 'Total Sales',
        total_sales_per_hour: 'Total Sales per Hour',
        total_sales_per_category: 'Total Sales per Category',
        total_sales_per_team: 'Total Sales per Team',
        no_team: 'No team',
        agent_leaderboard: 'Agent Leaderboard',
        agent: 'Agent',
        team: 'Team',
        location: 'Location',
        date: 'Date',
        product_board: 'Product Leaderboard',
        category: 'Category',
        product: 'Product',
        sales: 'Sales',
        time: 'Time',
        position: 'Pos.',
        last_update: 'Last Update'
    },
    kpiManager: {
        errors: {
            SomeError: 'An error occurred. Please try again.',
            UnknownError: 'An unknown error has occurred.'
        },
        table: {
            name: 'Name',
            createdAt: 'Created At',
            teams: 'Teams',
            noData: 'No data available',
            archived: 'Archived',
            edit: 'Edit',
            close: 'Close'
        },
        project: {
            add: 'Add Project',
            provideValue: 'Please provide a value',
            name: 'Project Name',
            archive: 'Archive Project',
            products: 'Products',
            product: 'Product',
            teams: 'Teams',
            category: 'Category',
            save: 'Save',
            close: 'Close',
            closeUnsavedAlert: 'Are you sure you want to close this without saving? Unsaved changes will be lost.',
            new: 'New',
            teamName: 'Team',
            locationName: 'Location'
        },
        displayArchive: 'Display archive',
        saveSuccessful: 'Save successful'
    },
    qualityManagement: {
        addEvaluation: 'Add Evaluation',
        noEvaluations: 'This call has no evaluations yet',
        dateRange: 'Date Range',
        backToList: 'Back',
        export: 'Export',
        exporting: 'Exporting...',
        teamsFilter: 'Teams',
        autoQaWentWrong: 'Something went wrong while generating the entity.',
        changesSaved: 'Changes saved successfully!',
        exportFailed: 'Export failed. Please try again later.',
        exportLimitWaringTitle: 'Export Limit Reached',
        exportLimitWaringText:
            'The number of evaluations exceeds the limit of 10000. Please adjust your filters to reduce the number of evaluations.',
        table: {
            id: 'ID',
            template: 'Template',
            project: 'Project',
            callId: 'Call ID',
            callDateTime: 'Call Date',
            status: 'Status',
            evaluatedUser: 'Agent',
            coachComment: 'Coach Comment',
            createdAt: 'Created At',
            createdBy: 'Created By',
            updatedAt: 'Updated At',
            updatedBy: 'Updated By',
            createdByAI: 'Automated Evaluation',
            createdByNoneAI: 'None Automated Evaluation',
            score: 'Average score'
        },
        form: {
            general: 'General',
            information: 'Information',
            coachingSheet: 'Questionnaire',
            agent: 'Agent',
            callId: 'Call ID',
            callDate: 'Call Date',
            comment: 'Coach Comment'
        },
        view: {
            title: 'Evaluation Details',
            edit: 'Edit',
            close: 'Close',
            deleteText: 'Are you sure you wanna delete this?',
            deleteTitle: 'Please confirm',
            cancel: 'Cancel',
            delete: 'Delete'
        },
        templates: 'Templates',
        reports: 'Reports',
        evaluations: 'Evaluations',
        filters: 'Filters',
        reset: 'Reset',
        saveEvaluation: 'Save Evaluation',
        templateNotFound: 'Template Not Found',
        templateTable: {
            id: 'ID',
            title: 'Template',
            questionnaire: 'Questions',
            llmPrologue: 'LLM Prologue',
            callsNotEvaluated: 'Calls not evaluated',
            callsEvaluated: 'Calls evaluated'
        },
        editTemplate: {
            title: 'Template Details',
            template: 'Template',
            close: 'Close',
            name: 'Template Name',
            llmPrologue: 'LLM Prologue',
            llmProviderName: 'LLM Provider Name',
            coachingSheet: 'Questionnaire',
            automationRules: 'Automation rules',
            addTeamTask: 'Add Team Task',
            team: 'Team',
            validFrom: 'Valid From',
            validTo: 'Valid To',
            minDurationSec: 'Minimum Call Duration',
            minCharsLen: 'Minimum text length',
            createAssignment: 'Create Task',
            noTasks: 'No tasks',
            sec: 'Sec',
            status: 'Status',
            editAssignment: 'Edit Task',
            addAssignment: 'Add Task'
        },
        stats: {
            averageScore: 'Average Score',
            totalEvaluations: 'Total Evaluations'
        },
        aggregateBy: 'Aggregate By',
        aggregate: {
            byDay: 'Day',
            byWeek: 'Week',
            byMount: 'Mount'
        },
        teamTaskStatus: {
            not_started: 'Not Started',
            completed: 'Completed',
            in_progress: 'In Progress',
            error: 'Error'
        }
    },
    dashboard: {
        paging: '%{range} von %{count} %{entity} werden angezeigt',
        pagingEntityNames: {
            [PAGING_ENTITIES.agents]: 'Agents',
            [PAGING_ENTITIES.agencies]: 'Agencies',
            [PAGING_ENTITIES.locations]: 'Location',
            [PAGING_ENTITIES.objects]: 'Objects',
            [PAGING_ENTITIES.team]: 'Teams'
        },
        groupByEntityNames: {
            [GROUP_BY.ALL]: 'All',
            [GROUP_BY.AGENCY]: 'Agency',
            [GROUP_BY.TEAM]: 'Team',
            [GROUP_BY.LOCATION]: 'Location',
            [GROUP_BY.AGENT]: 'Agent'
        },
        callHistory: 'CHECK CALLS',
        report: 'REPORT VIEW',
        fullScreen: 'FULL SCREEN',
        datepicker: {
            select: 'Select',
            cancel: 'Cancel',
            presetHeader: 'Presets',
            presetOptions: {
                thisWeek: 'This week',
                last7Days: 'Last 7 Days',
                last28Days: 'Last 28 Days',
                lastMonth: 'Last Month',
                last90Days: 'Last 90 Days'
            }
        },
        popups: {
            keywordsUsage: {
                header: 'Keyword Usage',
                messageSaid:
                    'Avg. Do-Say Usage = Percentage of keyword groups that were mentioned in a call on average',
                messageGroups:
                    'Usage of Keyword Groups = Percentage of calls this keyword group (at least one keyword) was mentioned',
                messageUsed: 'Keyword Usage = Percentage of Calls were an individual keyword was used'
            },
            speechRatio: {
                header: 'Speech Data',
                message: 'Speech Ratio = Shows the average agent speech ratio in a call',
                messageTempo: 'Speech Rate = Shows the number of words per minute'
            },
            privacyPopup: {
                message: 'The underlying data does not meet the data security criteria and cannot be displayed.'
            },
            privacyContentBox: {
                message:
                    'Due to the existing criteria for ensuring data privacy, it is possible that not all data will be displayed in full.'
            },
            dataRoomContentBox: {
                message:
                    'Due to the activated data room filter, only the data for calls with recording is displayed, which leads to a reduction of the total amount of data.'
            }
        },
        header: {
            dateRange: 'Date Range',
            participator: 'Participant'
        },
        cards: {
            errors: {
                header: 'Sorry, something is not working.',
                message: 'We are already aware of the issue, and are working on a solution.',
                privacyError: 'The underlying data does not meet the data privacy criteria and cannot be displayed.'
            },
            general: {
                ratedCallsCount: 'Evaluated Calls',
                identifiedCallsCount: 'Identified Calls',
                createTeam: 'Create Team',
                editTeam: 'Edit Team',
                myTeam: 'All',
                myLocation: 'All Teams',
                noData: 'No data is available in the selected period'
            },
            tooltips: {
                advancedQualityIndex: {
                    header: 'Advanced Main Criteria',
                    dialog: 'Conversational Skills',
                    social: 'Social & Communication Skills ',
                    script: 'Professional & Process Skills'
                },
                basicQualityIndex: {
                    header: 'Basic Main Criteria',
                    interview: 'Conversational Skills',
                    speech: 'Sales Pitch',
                    finish: 'Finalize a Sale'
                },
                callsWithPotential: {
                    title: 'Calls mit Potential'
                }
            },
            sections: {
                [CARD_SECTIONS.overview]: 'Overview',
                [CARD_SECTIONS.agencies]: 'Agencies',
                [CARD_SECTIONS.location]: 'Locations',
                [CARD_SECTIONS.agent]: 'Agents',
                [CARD_SECTIONS.team]: 'Team',
                [CARD_SECTIONS.teams]: 'Teams',
                [CARD_SECTIONS.agency]: 'Agencies',
                [CARD_SECTIONS.sentiment]: ''
            },
            headers: {
                [cardTypes.overviewWords]: 'Keyword Usage',
                [cardTypes.overviewAgencyWords]: '%{agencyName}’s Keyword Usage',
                [cardTypes.overviewAgentWords]: '%{agentName}’s Keyword Usage',
                [cardTypes.agenciesWords]: 'Agencies Keyword Usage',
                [cardTypes.agencyAgentsWords]: 'Agent’s Keyword Usage',
                [cardTypes.locationWords]: 'Locations Keyword Usage',
                [cardTypes.locationAgentsWords]: 'Agent’s Keyword Usage',
                [cardTypes.overviewLocationTeamsWords]: 'Team Keyword Usage',
                [cardTypes.agencyLocationsWords]: '%{agencyName}’s Keyword Usage',

                [cardTypes.overviewSpeechRatio]: 'Speech Data',
                [cardTypes.overviewAgencySpeechRatio]: '%{agencyName}’s Speech Data',
                [cardTypes.overviewAgentSpeechRatio]: '%{agentName}’s Speech Data',
                [cardTypes.agenciesSpeechRatio]: 'Agencies Speech Data',
                [cardTypes.agencyAgentsSpeechRatio]: 'Agent’s Speech Data',
                [cardTypes.locationSpeechRatio]: 'Locations Speech Data',
                [cardTypes.locationTeamsSpeechRatio]: 'Team Speech Data',
                [cardTypes.locationAgentsSpeechRatio]: 'Agent’s Speech Data',
                [cardTypes.agencyLocationsSpeechRatio]: '%{agencyName}’s Speech Data',

                [cardTypes.overviewCallDurationStats]: '%{companyName} Call Duration',
                [cardTypes.overviewAgencyCallDurationStats]: '%{agencyName} Call Duration',
                [cardTypes.overviewAgentCallDurationStats]: '%{agentName} Call Duration',
                [cardTypes.agencyCallDurationStats]: 'Agency Call Duration',
                [cardTypes.locationCallDurationStats]: 'Location Call Duration',
                [cardTypes.locationTeamsCallDurationStats]: 'Team Call Duration',

                [cardTypes.overviewCallAmount]: '%{companyName} Call Amount',
                [cardTypes.overviewAgencyCallAmount]: '%{agencyName} Call Amount',
                [cardTypes.overviewAgentCallAmount]: '%{agentName} Call Amount',
                [cardTypes.agenciesCallAmount]: 'Agencies Call Amount',
                [cardTypes.locationCallAmount]: 'Locations Call Amount',
                [cardTypes.overviewLocationTeamsCallAmount]: 'Team Call Amount',
                [cardTypes.agencyLocationsCallAmount]: '%{agencyName} Call Amount',

                [cardTypes.gamificationScorePoints]: 'i2x Points',
                [cardTypes.liveAgents]: 'Live Agents',
                [cardTypes.sentimentTeamComparison]: 'Team Comparison',
                [cardTypes.sentimentTrendAgent]: "Trend (Agent's Sentiment)",
                [cardTypes.sentimentTrendCustomer]: "Trend (Customer's Sentiment)"
            },

            [cardTypes.overviewWords]: overviewWordsCardConfig(),
            [cardTypes.overviewAgencyWords]: overviewWordsCardConfig(),
            [cardTypes.overviewAgentWords]: overviewWordsCardConfig(),
            [cardTypes.agenciesWords]: wordsCardConfig('Agency'),
            [cardTypes.agencyAgentsWords]: wordsCardConfig('Agent'),
            [cardTypes.locationWords]: wordsCardConfig('Location'),
            [cardTypes.locationAgentsWords]: wordsCardConfig('Agent'),
            [cardTypes.overviewLocationTeamsWords]: overviewWordsCardConfig(),
            [cardTypes.agencyLocationsWords]: wordsCardConfig('Location'),

            [cardTypes.overviewSpeechRatio]: overviewSpeechRatioCardConfig(),
            [cardTypes.overviewAgencySpeechRatio]: overviewSpeechRatioCardConfig(),
            [cardTypes.overviewAgentSpeechRatio]: overviewSpeechRatioCardConfig(),
            [cardTypes.agenciesSpeechRatio]: speechRationCardConfig('Agency'),
            [cardTypes.agencyAgentsSpeechRatio]: speechRationCardConfig('Agent'),
            [cardTypes.locationSpeechRatio]: speechRationCardConfig('Location'),
            [cardTypes.locationAgentsSpeechRatio]: speechRationCardConfig('Agent'),
            [cardTypes.locationTeamsSpeechRatio]: speechRationCardConfig('Team'),
            [cardTypes.agencyLocationsSpeechRatio]: speechRationCardConfig('Location'),

            [cardTypes.overviewCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.overviewAgencyCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.overviewAgentCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.agencyCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.locationCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.locationTeamsCallDurationStats]: callDurationStatsConfig(),

            [cardTypes.overviewCallAmount]: callAmountConfig(),
            [cardTypes.overviewAgencyCallAmount]: callAmountConfig(),
            [cardTypes.overviewAgentCallAmount]: callAmountConfig(),
            [cardTypes.agenciesCallAmount]: callAmountConfig(),
            [cardTypes.locationCallAmount]: callAmountConfig(),
            [cardTypes.overviewLocationTeamsCallAmount]: callAmountConfig(),
            [cardTypes.agencyLocationsCallAmount]: callAmountConfig(),

            [cardTypes.gamificationScorePoints]: gamificationScorePointsConfig(),
            [cardTypes.liveAgents]: {
                columns: {
                    name: 'Agent',
                    timestamp: 'Last Update',
                    status: 'Status'
                },
                status: {
                    unknown: 'Unknown',
                    inactive: 'Inactive',
                    'logged-in': 'Logged In',
                    'in-call': 'In Call'
                }
            },

            [cardTypes.sentimentTeamComparison]: sentimentTeamComparisonConfig(),
            [cardTypes.sentimentTrendAgent]: sentimentTrendCustomerConfig(),
            [cardTypes.sentimentTrendCustomer]: sentimentTrendCustomerConfig()
        },
        callsDashboard: {
            removeCardTitle: 'Remove card',
            addNewCard: 'Add a new card',
            addNewCardNote:
                'To create a new preset, go to Call History, set the desired filters, and save them as a new preset.',
            selectFilterTitle: 'Select a filter',
            removeModal: {
                title: 'Remove card?',
                content: 'Are you sure you want to remove this card?',
                cancel: 'Cancel',
                confirm: 'Remove'
            },
            moveCardUp: 'Move card up',
            moveCardDown: 'Move card down'
        },
        liveDashboard: {
            rank: 'Rank',
            agent: 'Agent',
            points: 'i2x Points Rate',
            pointsDescription: 'Points captured out of points possible',
            dontSayRate: "Don't Say Rate",
            calls: 'Calls',
            conversionRate: 'Conversion Rate',
            conversionRateDescription: 'Points per call (compared against team average)',
            status: 'Status',
            statusGreen: 'On Target',
            statusYellow: 'Unstable',
            statusRed: 'Critical',
            avgPerAgent: 'Ø Per Agent',
            perAgent: 'Per Agent',
            totalCalls: 'Total Calls',
            last2h: 'Last 2 hours',
            today: 'Today'
        }
    },
    notifications: {
        error: {
            general: 'Oops... something went wrong',
            [errorCodes.REQUEST_ERROR]: `Oops! Please try refreshing the page to see the results.`
        }
    },
    reports: {
        filters: {
            time: {
                textField: {
                    label: 'Time Frame',
                    placeholder: 'Select.'
                }
            },
            report: {
                textField: {
                    label: 'Report',
                    placeholder: 'Select'
                },
                selectValues: {
                    serviceScorecard: 'Basic Scorecard',
                    productScorecard: 'Advanced Scorecard',
                    keywordUse: 'Keyword Usage',
                    keywordGuidance: 'Keyword Usage',
                    keywordMoments: 'Moments'
                }
            },
            groups: {
                [reportGroup.all]: {
                    singular: '%{companyName} Overview',
                    plural: '%{companyName} Overview'
                },
                [reportGroup.agencies]: {
                    singular: 'Agency',
                    plural: 'Agencies'
                },
                [reportGroup.locations]: {
                    singular: 'Location',
                    plural: 'Locations'
                },
                [reportGroup.team]: {
                    singular: 'Team',
                    plural: 'Teams'
                },
                [reportGroup.agents]: {
                    singular: 'Agent',
                    plural: 'Agents'
                }
            },
            view: {
                textField: {
                    label: 'Overview',
                    placeholder: 'Select'
                },
                searchField: {
                    [reportViews.agents]: {
                        label: 'Your selection',
                        placeholder: 'Search for agents'
                    }
                },
                selectValues: {
                    overview: '%{companyName} Overview',
                    team: 'Team Comparison',
                    oneTeam: 'My Team',
                    location: 'All',
                    agencies: 'Agencies...',
                    locations: 'Locations...',
                    agents: 'Select Agents...',
                    agent: 'My Statistics',
                    agencyOverview: '%{agencyName} Overview',
                    teams: 'Select Teams...'
                },
                modal: {
                    title: {
                        [reportViews.all]: 'All',
                        [reportViews.team]: 'One team',
                        [reportViews.agencies]: 'Select one or more agencies',
                        [reportViews.locations]: 'Select one or more locations',
                        [reportViews.agents]: 'Select one or more agents',
                        [reportViews.teams]: 'Select one or more teams'
                    },
                    subtext: {
                        [reportViews.all]: 'No filter applied',
                        [reportViews.team]: 'One team only',
                        [reportViews.agencies]:
                            'Selecting more than one agency will create a comparison report across those agencies',
                        [reportViews.locations]:
                            'Selecting more than one location will create a comparison report across those locations ',
                        [reportViews.agents]:
                            'Selecting more than one agent will create a comparison report across those agents',
                        [reportViews.teams]:
                            'Selecting more than one team will create a comparison report across those teams'
                    },
                    buttons: {
                        clear: 'Clear',
                        apply: 'Apply'
                    },
                    selectAll: {
                        [reportViews.agencies]: 'All Agencies',
                        [reportViews.locations]: 'All Locations',
                        [reportViews.agents]: 'All Agents',
                        [reportViews.teams]: 'All Teams'
                    },
                    selectAllGroup: {
                        [reportViews.locations]: 'All %{groupName} Locations',
                        [reportViews.teams]: 'All %{groupName} Teams',
                        [reportViews.agents]: 'All %{groupName} Agents'
                    },
                    agentsModal: {
                        agentsFrom: 'Agenten from'
                    },
                    empty: 'No options available',
                    searchPlaceholder: 'Search...',
                    noResults: 'No results found'
                },
                choiceLabel: {
                    one: 'Agenturen - %{name}',
                    more: '%{number} Agenturen'
                },
                label: {
                    [reportViews.locations]: {
                        one: 'Locations - %{name}',
                        more: '%{number} Locations'
                    },
                    [reportViews.agencies]: {
                        one: 'Agencies - %{name}',
                        more: '%{number} Agencies'
                    },
                    [reportViews.agents]: {
                        one: 'Agents - %{name}',
                        more: '%{number} Agents'
                    },
                    [reportViews.teams]: {
                        one: 'Teams - %{name}',
                        more: '%{number} Teams'
                    }
                }
            },
            member: {
                textField: {
                    label: 'Member',
                    placeholder: 'Select'
                },
                selectValues: {
                    all: 'All Agents (%{number})',
                    agents: 'Agents (%{number})',
                    trainees: 'Trainees (%{number})'
                }
            },
            button: 'Create Report',
            dataRoomToggleOn: 'Recorded Calls Only.',
            dataRoomToggleOff: 'All Calls.'
        },
        tables: {
            [tableTypes.KEYWORD_GUIDANCE]: {
                label: {
                    one: 'Keyword Usage: %{selection}',
                    many: 'Keyword Usage Comparison: %{selection}'
                },
                cells: {
                    stats: {
                        ratedCalls: 'Rated Calls',
                        [REPORT_GUIDANCE_TYPES.whitelists]: {
                            overallStats: 'Do-Say Usage'
                        },
                        [REPORT_GUIDANCE_TYPES.blacklists]: {
                            overallStats: 'Don’t-Say Usage'
                        },
                        wordgroup: 'Word Group'
                    }
                }
            },
            scorecard: {
                cells: {
                    samples: 'Samples',
                    overview: 'Overview',
                    weight: 'Weight'
                }
            },
            emptyState: {
                title: 'Create detailed reports',
                subtitle: 'Use the drop-downs above to customize your desired report'
            },
            noDataState: {
                title: 'There is no data available for the selected time frame',
                subtitle: ''
            },
            privacy: {
                cell: 'hidden'
            },
            sumColumns: {
                title: 'Sum'
            }
        }
    },
    filters: {
        agents: {
            // TODO: show agents count when backend is ready (%{count})
            all: 'All Agents',
            agents: 'Agents',
            trainees: 'Trainees',
            textField: {
                label: 'Agent',
                placeholder: 'Select...'
            }
        },
        topic: {
            all: 'All Topics',
            frequent: 'Frequent Topics',
            select: 'Select call topics...',
            textField: {
                label: 'Call Topic',
                placeholder: 'Select...'
            }
        },
        callType: {
            ...callTypes,
            textField: {
                label: 'Call Type',
                placeholder: 'Select...'
            }
        },
        teamId: {
            label: 'Team',
            all: 'All Teams',
            textField: {
                label: 'Team',
                placeholder: 'Select...'
            }
        },
        conversion: {
            all: 'Conversion',
            take: 'Calls with conversion',
            noTake: 'Calls without conversion',
            textField: {
                label: 'Conversion',
                placeholder: 'Select...'
            }
        },
        reportType: {
            label: 'Report',
            placeholder: 'Select...'
        },
        speakerType: {
            label: 'Speaker',
            placeholder: 'Select...'
        },
        granularity: {
            day: 'Daily',
            week: 'Weekly',
            month: 'Monthly',
            quarter: 'Quarterly',
            year: 'Yearly'
        },
        agencies: {
            all: 'All Agencies'
        },
        locations: {
            all: 'All Locations'
        },
        locationAndAgency: {
            all: 'Vodafone'
        },
        hierarchySelection: {
            agency: 'Agencies...',
            location: 'Locations...',
            team: 'Teams...',
            agent: 'Certain Agents...',
            selected: {
                agency: {
                    plural: '%{selection} Agencies',
                    singular: '%{selection} Agency'
                },
                location: {
                    plural: '%{selection} Locations',
                    singular: '%{selection} Location'
                },
                team: {
                    plural: '%{selection} Teams',
                    singular: '%{selection} Team'
                },
                agent: {
                    plural: '%{selection} Agents',
                    singular: '%{selection} Agent'
                }
            }
        }
    },
    common: {
        sentiment: 'Sentiment',
        sentimentNoCalls: 'No calls with sentiment',
        allTeams: 'All Teams',
        beta: 'BETA',
        quarter: 'Quarter',
        calls: 'Calls',
        appInitializationFailed: {
            title: 'Application initialization failed',
            subTitle: 'Please try reloading after sometime. We are already working on fixing this issue.',
            button: 'Try again'
        },
        failedState: {
            title: 'Something went wrong',
            subtitle: 'Try again later'
        },
        agentActivityStatus: {
            [AGENT_ACTIVITY_STATUS.ANY]: 'Any',
            [AGENT_ACTIVITY_STATUS.ACTIVE]: 'Active',
            [AGENT_ACTIVITY_STATUS.INACTIVE]: 'Inactive'
        },
        roles: {
            [USER_ROLES.CORE_AGENT]: 'Agent',
            [USER_ROLES.CORE_TEAM_LEAD]: 'Team Lead',
            [USER_ROLES.LOCATION]: 'Location Manager',
            [USER_ROLES.AGENCY]: 'Agency Manager',
            [USER_ROLES.COMPANY]: 'Company Manager',
            [USER_ROLES.COMPANY_EXTERNAL]: 'Company Manager [+]',
            i2x_admin: 'Supervisor',
            teamLead: 'Team Lead'
        },
        discardDialog: {
            title: 'Discard Changes?',
            content: `Changes you made won’t be saved.`,
            confirm: 'Discard',
            cancel: 'Cancel'
        },
        emptyTeamsMessage: 'You don’t have any teams',
        save: 'Save',
        cancel: 'Cancel',
        confirm: 'Confirm'
    },
    languages: {
        en: 'English',
        de: 'German'
    },
    keywordManager: {
        subtitle: 'Add and manage the keywords that the agents use in the i2x trainer',
        language: 'Speaker language',
        teams: 'Teams',
        visibilityLabels: {
            agent: 'Agent Guidance',
            statistics: 'Moments'
        },
        newSet: 'New Set',
        deleteSet: 'Set löschen',
        setConfig: {
            speakerLabel: 'Speaker:',
            mention: 'mention',
            targets: {
                agent: 'Agents',
                client: 'Customers',
                both: 'Either'
            },
            typeLabels: {
                whitelist: 'should',
                blacklist: 'should not'
            }
        },
        importKeywords: {
            title: 'Import keywords',
            subtitle: 'You can add keywords from different teams',
            assignTitle: ' ASSIGN...',
            yourSelection: 'Your selection',
            cancelButton: 'CANCEL',
            confirmButton: 'IMPORT',
            from: 'from',
            doSaySection: 'Do-Say',
            info: 'In the main template you can create a collection of keywords or import keywords from an already existing Team. The keywords from the main template can then be assigned to one or more teams. Changes to the main template will NOT automatically affect teams sharing those keywords',
            dontSaySection: "Don't Say",
            imported: 'Keywords imported successfully!',
            validation: {
                similarKeywords: 'Similar keywords',
                similarKeywordsMessage: 'Similar keywords already exist!',
                similarKeywordsDescription:
                    'There are similar keywords that already exist in the current list. Therefore, they will not be imported into the team.'
            }
        },
        importTemplate: {
            templates: 'Templates',
            title: 'Assign to Team...',
            subtitle: 'You can assign these keywords to multiple teams.',
            cancelButton: 'CANCEL',
            confirmButton: 'IMPORT',
            info: 'In the main template, you can create a collection of keywords or import keywords from an already existing team. The keywords from the main template can then be assigned to one or more teams. Changes to the main template do NOT automatically affect teams that share these keywords.',
            overwriteWarning:
                'With this option activated, all existing keywords will be deleted from the selected team/s. Leave this option blank if you just want to add keywords from this template to the selected team/s.',
            replaceAllKeywords: 'Replace all keywords of selected teams with keywords from this template.',
            overwriteModal: {
                title: 'Are you sure?',
                content:
                    'This option will delete all existing keywords from the selected team(s) and replace them with the keywords from this template. This action cannot be undone!\n',
                confirm: 'DELETE AND REPLACE ALL KEYWORDS\n',
                cancel: 'CANCEL'
            }
        },
        tabs: {
            whitelist: 'Do-say list',
            blacklist: 'Don’t-say list',
            phraseDetection: 'Call Event Keywords'
        },
        header: {
            groupName: {
                text: 'Group Name',
                helpText: 'Title for this word group. (Not a spotting keyword).'
            },
            keyword: { text: 'Keyword Group', helpText: '' },
            relatedTerms: {
                text: 'Tracked Keywords',
                helpText:
                    'You can create related keywords or longer phrases that can be synonyms or different ways of expression.'
            },
            matchingLogic: {
                text: 'Matching Logic',
                helpText1: 'You can select different matching logic for a phrase group.',
                helpText2:
                    '- %{strong}Exact:%{_strong} this option requires Agent to say exactly the keywords or phrases.',
                helpText3:
                    '- %{strong}Flexible:%{_strong} this option gives Agent freedom to use different ways of expression within context. This option also include conjugation & declination of words.',
                helpText4: 'In case of conditional keywords, only exact matching logic is available.'
            }
        },
        content: {
            keywordGroup: 'Keyword Gruppe',
            singleKeyword: 'Einzelnes Keyword',
            placeholders: {
                addTopic: 'Enter group name...',
                keywordSetName: 'Keyword set name',
                addAlternative: 'Add keyword...',
                emptySetError: 'Please enter a keyword set name'
            },
            matchingLogic: {
                exactMatch: 'Strict',
                rephrasingAllowed: 'Flexible',
                disabledMessage: 'Only exact matching logic is available for conditional keywords.'
            }
        },
        validationMessages: {
            [VALIDATION_CODES.DUPLICATED_IN_WHITELIST]: 'This keyword is already in your „Do-Say“ list',
            [VALIDATION_CODES.DUPLICATED_IN_BLACKLIST]: 'This keyword is already in your „Dont-Say“ list',
            [VALIDATION_CODES.UNSUPPORTED_CHARS]: 'Keywords may only contain letters',
            [VALIDATION_CODES.UNSUPPORTED_NUM]: 'Keywords from one to ten must be written out',
            [VALIDATION_CODES.TOO_LONG]: 'This keyword is too long',
            [VALIDATION_CODES.GROUP_NAME_NOT_UNIQUE]: `This group name is already in your list`,
            [VALIDATION_CODES.KEYWORD_NOT_UNIQUE]: `This keyword is already in your list`,
            [VALIDATION_CODES.KEYWORD_COMBINATION_NOT_UNIQUE]: `This keyword combination is already in your list`,
            [VALIDATION_CODES.EMPTY_GROUP_NAME]: `Please enter a group name`,
            default: 'That keyword is not valid'
        },
        superWordMessage: `The Superword is highlighted with a star and worth %{superWordPoints} points`,
        actions: {
            save: 'Save',
            undo: 'Undo Changes',
            undoLastChange: 'Undo last change'
        },
        saveModal: {
            title: 'Discard changes?',
            content: 'You’re leaving the keyword manager without saving your changes.',
            cancel: 'Cancel',
            confirm: 'Discard changes'
        },
        confirmationMessage: {
            changesSaved: 'Changes saved successfully!',
            notifyAgents1: 'Agents will not be notified about these changes.',
            notifyAgents2: 'Please inform your agents if necessary.'
        },
        deleteConfirm: {
            title: 'Delete Keyword Set',
            content: 'Keyword Set and all of its keywords will be removed.'
        },
        keywordsLimitModal: {
            title: "Can't add more keywords",
            content: 'You have reached the limit of 200 keywords. Delete some keywords before adding new ones.',
            confirm: 'OK'
        },
        importSetsModal: {
            title: 'Import Keyword Configuration',
            subTitle: 'Importing configuration will overwrite any current configuration of the team.',
            previewSets: 'Select a team to preview the sets to be imported.',
            sets: 'Sets',
            whiteListSets: 'Do-Say sets',
            blackListSets: 'Don’t-Say sets',
            cancel: 'Cancel',
            confirm: 'Import',
            importedSuccess: 'Keyword configuration imported'
        },
        emptySets: {
            title: 'Start generating call insights and agent feedback with keywords!',
            subTitle: `Before i2x can display call feedback, you'll first need to create sets of Do-Say & Don’t-Say words`
        },
        conditionsModal: {
            discard: 'Cancel',
            heading: 'Track this keyword in utterance...',
            utteranceTooltip: `Every transcript is composed of short speech units called "utterances".  Utterances are uninterrupted chains of spoken words that can be as short as one word or as long as several sentences.`,
            rowPlaceholder: 'add words...',
            rowHeadings: {
                with: '...only with:',
                without: '...only without:',
                unless: "...unless it's:"
            },
            unsupportedWordsError: 'Some words can’t be transcribed and the condition won’t be applied',
            conditionsMaxWordsError: 'You can add max 25 words per condition',
            unlessWordsError: 'Unless condition must contain %{targetWord} to exclude phrase from being tracked'
        },
        teamSelectorLabel: 'Teams',
        teamSelectorLabelPlaceholder: 'Select team',
        updateKeywords: {
            [NOTIFICATION_TYPES.SUCCESS]: 'Keywords updated successfully',
            [NOTIFICATION_TYPES.ERROR]: 'Keywords update failed',
            [NOTIFICATION_TYPES.IMPORTED]: 'Keyword configuration imported',
            [NOTIFICATION_TYPES.ERROR_WHITELIST]: 'Keywords update failed: whitelist not valid',
            [NOTIFICATION_TYPES.ERROR_BLACKLIST]: 'Keywords update failed: blacklist not valid'
        },
        unsupportedTooltip: `Can’t transcribe this word correctly. It won’t be tracked and isn’t visible to agents.`,
        unsupportedModal: {
            title: 'Some keywords can’t be used',
            contentSingle: `The i2x speech recognition currently can’t transcribe this word correctly.`,
            contentPlural: `The i2x speech recognition currently can’t transcribe these %{count} words correctly.`,
            content1: `They won’t be added to the desktop assistant or tracked in the dashboard.`,
            content2: `To enhance the speech-to-text transcription by adding custom vocabulary (such as domain-specific terminology or brand names) please send your request to: %{email}`,
            confirm: 'OK'
        },
        matchingLogicLimits: {
            warningFlexibleKeywordsLimitExceeded: {
                title: 'This keyword is not eligible for flexible matching logic!',
                text: 'The flexible recognition logic is only available for “say” words with a maximum of two related terms. Make sure that this “say” word has only two related terms.',
                example: 'Example',
                keyword1: 'How can I help you',
                keyword2: 'what can I do for you today'
            },
            warningFlexibleKeywordNotEligible: {
                title: 'Limits for keywords with flexible matching logic exceeded!',
                text: 'Flexible matching logic is only available for a maximum of 3 keywords from “Keyword” column.'
            },
            noMoreKeywordsCanBeAdded: 'No more keywords can be added due to flexible matching logic'
        },
        phrasesDetection: {
            callStartKeyword: 'Call Start Keyword',
            callStopKeyword: 'Call Stop Keyword',
            keywordsNotProvided: {
                title: 'Call Event Keywords Missing',
                text: 'No keywords have been provided. Please enter the required keywords, including the Call Start / Stop Keyword, to proceed.'
            }
        }
    },
    userManager: {
        titleTeams: 'Team Management',
        titleUsers: 'User Management',
        create: 'Create Team',
        createTeam: 'Create Team',
        createUser: 'Create User',
        importUsers: 'Import Users',
        listUsers: 'List Users',
        listTeams: 'List Teams',
        teamNameLabel: 'Team name',
        saveNew: 'Create',
        update: 'Save Changes',
        discard: 'Discard',
        delete: 'Delete Team',
        createTeamTitle: 'Create a team',
        updateTeamTitle: 'Edit team',
        createUserTitle: 'Create a user',
        updateUserTitle: 'Edit user',
        error: 'Operation failed. Please try again later.',
        searchUsers: 'Search Users',
        searchTeams: 'Search Teams',
        showDissociatedUsers: 'Show deactivated users',
        selectTeam: 'Select Team',
        deactivated: 'Deactivated',
        deactivatedTooltip: 'This user is deactivated and will not be able to log in',
        unknown: 'Unknown',
        validation: {
            emptyTeamName: 'Please enter a name for your team.',
            duplicatedTeamName: 'A team with this name already exists. Please choose a different name for your team.',
            teamNameTooLong: 'Team name should be no more than %{max} characters long'
        },
        tooltips: {
            agents: `An agent can belong to one team only. A team can have multiple Team Leads.`,
            editTeam: 'Add or Remove team members',
            editUser: 'Add or Remove user',
            team: 'Please add at least one team lead to this team',
            teamNotValid: 'Failed to update, please contact support.'
        },
        locationUsersList: 'Add team members',
        teamMembersList: 'Team members',
        emptyTeam: 'No team members added',
        emptyLocationUsers: 'All agents are already assigned to a team',
        saveNewTeamSuccess: 'Team created successfully',
        saveNewTeamFail: {
            [errorCodes.REQUEST_ERROR]: 'Team couldn’t be created',
            [errorCodes.userManager.TEAM_NAME_NOT_UNIQUE]:
                `A team with this name already exists. Please choose a different name for your team.`
        },
        updateTeamSuccess: 'Changes saved successfully',
        updateTeamFail: {
            [errorCodes.REQUEST_ERROR]: 'Changes couldn’t be saved',
            [errorCodes.userManager.TEAM_NAME_NOT_UNIQUE]:
                `A team with this name already exists. Please choose a different name for your team.`
        },
        saveEmptyDialog: {
            title: 'No team members',
            content: 'You haven’t added any team members to this team, create anyway?',
            confirm: 'Create',
            cancel: 'Cancel'
        },
        discardNewDialog: {
            title: 'Discard team?',
            content: 'The team won’t be created, you will lose all the progress.',
            confirm: 'Discard',
            cancel: 'Cancel'
        },
        discardUpdateDialog: {
            title: 'Discard team?',
            content: `Changes you made won’t be saved.`,
            confirm: 'Discard',
            cancel: 'Cancel'
        },
        deleteDialog: {
            title: 'Delete team',
            content: `This team and all associated keywords will be permanently deleted. This action cannot be undone.`,
            confirm: 'Delete',
            cancel: 'Cancel',
            errorTeamNotEmpty: 'Please remove all team members first.',
            deleteTeamFail: 'Failed to delete team'
        },
        columns: {
            label: 'Team',
            managerNames: 'Team Lead(s)',
            agentsCount: 'Agents',
            activeLastWeek: 'Active last week',
            activeLastMonth: 'Active last month',
            lastCallDate: 'Last Call',
            lastLoginDate: 'Last Login',
            neverLoggedIn: 'Never logged in',
            role: 'Role',
            agent: 'Name',
            telephonyId: 'Telephony Login ID',
            password: 'Password',
            email: 'E-Mail',
            id: 'ID',
            name: 'Name',
            companyUsername: 'Username',
            language: 'Language',
            dateJoined: 'Join Date',
            passwordChangedAt: 'Password Changed At',
            status: 'Status',
            team: 'Team',
            teams: 'Teams',
            firstName: 'First Name',
            lastName: 'Last Name'
        },
        telephonyId: {
            tooltipHelperText: {
                paragraph1: 'The Telephony Login ID is the ID that the agent uses to login into the phone system.',
                paragraph2:
                    'Every active agent needs an ID. Without a Telephony Login ID, i2x Assistant cannot receive calls.'
            },
            delete: {
                title: 'Are you sure you want to remove this ID?',
                body: 'Without a Telephony Login ID, i2x Assistant cannot receive calls',
                confirmButtonText: 'Remove'
            },
            addId: 'Add ID',
            editId: 'Edit ID',
            removeId: 'Remove ID',
            addedMessage: 'Telephony Login ID added',
            savedMessage: 'Telephony Login ID changes saved',
            removedMessage: 'Telephony Login ID removed',
            errorMessage: 'Telephony login ID not saved',
            errorInUseMessage: 'Telephony Login ID already in use',
            validationMessage: 'The Telephony Login ID should only consist of numbers',
            duplicateIdMessage: 'This Telefonie Login ID is already assigned to another agent',
            saveFailedMessage: 'Failed to save Telephony Login ID'
        },
        password: {
            addId: 'Set Password',
            savedMessage: 'Password saved',
            errorMessage: 'Password not saved',
            validationMessageUnknownError: 'Unfortunately, an unknown error has occurred.',
            validationMessagePasswordTooWeak: 'Password is too weak.',
            tip: 'Password should include uppercase and lowercase letters, numbers, and symbols. Avoid common words and patterns. Make it longer and more complex for better security.',
            roleNotSupported: 'For this role, please use the password reset form on the login page.'
        },
        companyUsername: {
            validationMessage: 'Please enter a valid username.',
            duplicateIdMessage: 'This username is already assigned to another user',
            saveFailedMessage: 'Failed to save username',
            editId: 'Edit username',
            addId: 'Add username'
        },
        validationMessages: {
            emailRequired: 'Please enter your email address.',
            emailInvalid: 'Please enter a valid email address.',
            emailExists: 'This email is already in use.',
            firstNameRequired: 'Please enter your first name.',
            firstNameTooLong: 'First name should be no more than 30 characters long.',
            lastNameRequired: 'Please enter your last name.',
            lastNameTooLong: 'Last name should be no more than 30 characters long.',
            roleRequired: 'Please select a role.',
            companyUsernameRequired: 'Please enter a username.',
            companyUsernameInvalid: 'Please enter a valid username.',
            companyUsernameExists: 'This username is already in use.',
            companyUsernameTooLong: 'Username should be no more than 60 characters long.',
            teamNotFound: 'Team not found'
        },
        errors: {
            userNotFound: 'User not found',
            failedToSaveUser: 'Failed to save user'
        },
        userCrudMessages: {
            updateUserSuccess: 'User updated successfully',
            createUserSuccess: 'User created successfully',
            updateUserError: 'Failed to update user',
            createUserError: 'Failed to create user',
            deleteUserSuccess: 'User deleted successfully',
            deleteUserError: 'Failed to delete user'
        },
        status: {
            ACTIVE: 'Active',
            INCIDENTAL: 'Incidental',
            DISASSOCIATED: 'Disassociated',
            TO_DISASSOCIATE: 'To disassociate'
        },
        defaultTeamMessage: 'All new agents are automatically added to the team that was created first',
        userListSpecialFilter: {
            title: 'Special Filter',
            none: 'None',
            withoutLogin: 'Without Login',
            withoutTeam: 'Without Team',
            lastLoginMoreThan30DaysAgo: 'Last Login More Than 30 Days Ago',
            withoutCalls: 'Without Calls',
            lastCallMoreThan30DaysAgo: 'Last Call More Than 30 Days Ago'
        },
        import: {
            title: 'Import users',
            columns: {
                status: 'Status'
            },
            fields: {
                email: 'Email',
                companyUsername: 'Company Username',
                firstName: 'First Name',
                lastName: 'Last Name',
                team: 'Team',
                password: 'Password',
                skip: 'Skip column'
            },
            buttons: {
                saveAll: 'Save all',
                savingAll: 'Saving...',
                newImport: 'New import',
                parseData: 'Parse data'
            },
            mappedReady: 'Ready',
            mappedMissingRequired: 'Missing required fields',
            pasteInstructions:
                'Copy data from Excel or Sheets and paste it below. Columns will be detected automatically.',
            pastePlaceholder: 'Paste tabular data here...',
            alerts: {
                mapRequiredFields: 'Please map Email and Company Username columns to proceed.'
            },
            tooltips: {
                rowHasErrors: 'Row contains errors',
                saveRow: 'Save this user',
                removeRow: 'Remove this row'
            },
            confirmRemoveRow: 'Remove this row from the import?',
            confirmReset: 'Clear all imported data and start a new import?',
            errors: {
                saveFailed: 'Save failed',
                validationFailed: 'Validation failed'
            }
        }
    },
    pageNotFound: {
        title: 'Page not found...',
        subTitle: 'That would never happen in a call.',
        buttonText: 'Go to Home Page'
    },
    callHistoryMaintenance: {
        title: 'Call History is temporarily not available',
        subTitle: 'i2x engineers are actively working on it. Please try again later. Sorry for the inconvenience.'
    },
    snackbar: {
        messages: {
            applied: 'Changes applied successfully',
            saved: 'Changes saved successfully',
            requestFail: 'Something went wrong while fetching from server', // TODO: Check message language.
            urlCopySuccess: 'Link copied to clipboard'
        }
    },
    callHistory: {
        deletion: {
            button: 'Delete call data',
            customer: 'Delete customer data',
            call: 'Delete entire call',
            customerConfirm:
                'Delete the customer transcript, sentiment analysis, and audio from this call? This cannot be undone.',
            callConfirm: 'Permanently delete this call and all associated data? This cannot be undone.',
            cancel: 'Cancel',
            confirm: 'Delete',
            error: 'Could not delete call data. Please try again.'
        },
        defaultTeamMessage:
            'Recordings of calls made between 15.01. and 17.01.2021 are currently not available. We are working hard to make the calls appear again as soon as possible.',
        general: {
            today: 'Today',
            yesterday: 'Yesterday',
            filters: 'Filters',
            beta: 'BETA'
        },
        navigation: {
            previous: 'Previous call',
            next: 'Next call'
        },
        filterButtons: {
            clear: 'Clean',
            apply: 'Apply'
        },
        searchSpeakerOptions: {
            agents: 'Agents',
            customers: 'Customers',
            any: 'Anyone'
        },
        tableHeaders: {
            callId: 'Call ID',
            callType: 'Type',
            botProject: 'Project',
            caller: 'Caller',
            agentName: 'Agent',
            teamName: 'Team',
            callTopics: 'Call Topics',
            doSay: 'Do-Say Usage',
            duration: 'Duration',
            callStart: 'Start Time',
            scorecard: 'Scorecard Rating',
            points: 'Points',
            customerSentiment: 'Customer Sentiment',
            agentSentiment: 'Agent Sentiment',
            sentimentHelpCustomer:
                'Sentiment reflects the emotional tone of your customer. This feature analyses the ratio of negative, positive, and neutral sentiment categories expressed by the customer during the call. It can take upto 1 hour before the analysis is available.',
            sentimentHelpAgent:
                'Sentiment reflects the emotional tone of your agent. This feature analyses the ratio of negative, positive, and neutral sentiment categories expressed by the agent during the call. It can take upto 1 hour before the analysis is available.',
            goToCall: 'Go to Call Page',
            sorting: {
                positiveAscending: 'Positive (ascending)',
                positiveDescending: 'Positive (descending)',
                negativeAscending: 'Negative (ascending)',
                negativeDescending: 'Negative (descending)',
                neutralAscending: 'Neutral (ascending)',
                neutralDescending: 'Neutral (descending)'
            }
        },
        download: {
            download: 'Download',
            transcript: 'Transcript',
            audio: 'Audio'
        },
        view: {
            title: 'Call Details',
            close: 'Close',
            createdBy: 'Created By',
            messageByUser: 'User',
            messageByBot: 'Bot',
            messageByAgent: 'Agent',
            duration: 'Duration',
            emptyTranscript: 'There is no transcript for the call'
        },
        filters: {
            [filterIds.callHistoryViewFilter]: {
                label: 'View'
            },
            [filterIds.callHistoryTimeRangeFilter]: {
                label: 'Call Duration',
                clearButton: 'Clear',
                maxValue: 'any'
            },
            [filterIds.callHistoryDoSayScoreFilter]: {
                label: 'Do Say Score'
            },
            dateRange: 'Date Range',
            callType: 'Type',
            caller: 'Caller',
            duration: 'Duration',
            projectName: 'Project'
        },
        callTopics: {
            label: 'Call Topics',
            empty: 'No topic',
            unavailable: ''
        },
        emptyState: {
            title: 'We couldn’t find any matching calls',
            text: 'Try modifying your search criteria '
        },
        sentiment: {
            positive: 'Positive',
            neutral: 'Neutral',
            negative: 'Negative',
            status: {
                processing: 'Processing',
                notAvailable: '-'
            }
        },
        callDetails: {
            doSaySection: 'Do-Say Usage',
            dontSaySection: "Don't Say Usage",
            transcript: 'Transcript',
            searchPlaceholder: 'Search this call...',
            customer: 'Customer',
            agent: 'Agent',
            emptyState: {
                title: 'There is no transcript for this call (yet).',
                text: 'Please note that the transcript is usually available about a minute after the call has ended. If more time has passed, you either stumbled upon an error or we were unable to record spoken words.'
            },
            player: {
                playbackSpeed: 'Playback speed',
                normal: '1x',
                fast: '1.5x',
                faster: '2x'
            },
            sentimentAnalysis: {
                title: {
                    customer: 'Customer sentiment',
                    agent: 'Agent sentiment'
                },
                tooltip: {
                    customer: 'This calculation is only based on the total number of utterances on the customer side.',
                    agent: 'This calculation is based solely on the total number of utterances on the agent side.'
                },
                positive: 'Positive',
                neutral: 'Neutral',
                negative: 'Negative'
            },
            feedback: {
                title: 'Report the sentiment rating for this utterance',
                subtitle:
                    'This utterance has been rated as positive. If you disagree, please select your own rating below. We recommend you read the entire utterance carefully before reporting.',
                choiceLabel: 'Please choose a rating you think is correct:',
                positive: 'Positive',
                neutral: 'Neutral',
                negative: 'Negative',
                additionalInformation: 'Please provide additional information: (optional)',
                submit: 'Submit',
                success: 'Thank you for your feedback.',
                report: 'Report sentiment rating'
            },
            callSummary: {
                copySuccess: 'Copied!',
                header: 'Summary',
                generateMessage:
                    'You can now generate a summary for this call. The quality of the summary might be affected if customer’s side is not recorded.',
                generateButton: 'Generate',
                generatingMessage: 'Generating...',
                errorMessage: 'The summary could not be loaded. Please try again later.',
                feedback: 'Is this summary helpful?',
                feedbackGiven: 'Thank you for your feedback!',
                copy: 'Copy'
            }
        },
        evaluation: {
            title: 'Call Evaluation',
            create: 'Create call entity',
            sidebarHeader: 'Call evaluations',
            backToList: 'Back to evaluations'
        },
        anonymization: {
            [ANONYMIZATION_TYPES.NUMBER]: 'number',
            [ANONYMIZATION_TYPES.ADDRESS]: 'address',
            [ANONYMIZATION_TYPES.NAME]: 'name',
            noType: 'anonymization'
        },
        transcriptSearch: {
            searchResultHeader: 'Displaying %{resultCount} of %{total} results in this call',
            searchResultHeaderOneResult: 'Displaying %{resultCount} result in this call',
            searchResultHeaderTwoResults: 'Displaying %{resultCount} results in this call',
            resultsCount: '%{totalCount} calls',
            viewCall: 'View Call',
            saidBy: 'Said By: ',
            searchPlaceholder: 'Search in calls...'
        },
        callHistoryScoreCardFilter:
            'The scorecard gets reviewed overnight. Use the filter to find calls that have already been rated.',
        preset: {
            new: 'Create new',
            changeFiltersToSave: 'Change the filters to save a new preset',
            title: 'Preset',
            static: 'Static',
            dynamic: 'Dynamic',
            staticNames: {
                'shorter-than-2-minutes': 'Shorter than 2 min.',
                'shorter-than-2-7-minutes': '2–7 min.',
                'longer-than-14-28-minutes': '14–28 min.',
                'longer-than-28-minutes': 'Over 28 min.'
            },
            removeModal: {
                title: 'Remove preset',
                content: 'Are you sure you want to remove this preset?',
                cancel: 'Cancel',
                confirm: 'Confirm'
            },
            saveModal: {
                title: 'Create new preset',
                inputLabel: 'Preset Name',
                cancel: 'Cancel',
                confirm: 'Save'
            }
        }
    },
    date: {
        short: {
            seconds: 's',
            minutes: 'm',
            hours: 'h'
        }
    },
    number: {
        percentage: '%{value}%'
    }
};
